import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Gavel as GavelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../utils/fetchJson';
import { isPastAcademicYear } from '../../utils/academicYearContext';
import useScopeAutoRefresh from '../../hooks/useScopeAutoRefresh';

const SEQ_OPTIONS = [1, 2, 3, 4, 5, 6];

const minutesToHHMM = (totalMinutes) => {
  const mins = Math.max(0, parseInt(totalMinutes || 0, 10) || 0);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const ClassDisciplinePage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);
  const [selectedSequenceNo, setSelectedSequenceNo] = useState(1);
  const [error, setError] = useState(null);
  const [classId, setClassId] = useState(null);
  const [isPrimaryClass, setIsPrimaryClass] = useState(false);

  // Dialog states
  const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
  // PAST YEARS ARE READ-ONLY. Last year's warnings are what last year's report
  // cards were printed from; editing one afterwards rewrites history nobody can
  // see being rewritten. The server refuses these writes now — this is what
  // stops a teacher filling in a form that was always going to be rejected.
  const pastYear = isPastAcademicYear();

  const [editingRow, setEditingRow] = useState(null);
  const [disciplineFormData, setDisciplineFormData] = useState({
    warnings: 0,
    justifiedLateness: 0,
    unjustifiedLateness: 0,
    detention: 0,
    suspension: 0
  });

  const loadSummary = useCallback(async (seqNo) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchJson(`/api/class-discipline/summary/sequence?sequenceNo=${seqNo}`);
      setSummaryRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error('Error loading discipline summary:', err);
      setError(err.message || 'Failed to load discipline data');
      setSummaryRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClasses = useCallback(async () => {
    try {
      setError(null);
      const classes = await fetchJson('/api/teacher/classes');
      if (!Array.isArray(classes) || classes.length === 0) {
        setError(t('classDisciplinePage.errors.noAssignedClass'));
        setLoading(false);
        return;
      }
      const chosen = classes.find((c) => (c?.level || '').toLowerCase() !== 'nursery') || null;
      if (!chosen) {
        setError(t('classDisciplinePage.errors.nurseryNotSupported'));
        setLoading(false);
        return;
      }
      setClassId(chosen.id);
      setIsPrimaryClass((chosen?.level || '').toLowerCase() === 'primary');
    } catch (err) {
      setError(t('classDisciplinePage.errors.fetchData'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  // THE YEAR SWITCHER HAS TO REACH THIS PAGE.
  //
  // Running a class does not stop at the current year: last year's warnings are
  // what last year's report cards were printed from, and a class master is
  // exactly the person who needs to look them up. The switcher was in the top
  // bar and this page ignored it, so choosing a finished year changed the
  // heading and nothing else — the same rows stayed on screen under a different
  // year, which is worse than showing nothing, because it looks like an answer.
  //
  // Both loads re-run: the class list is itself scoped, so a class the teacher
  // ran only in a past year does not appear until the year is asked for.
  useScopeAutoRefresh(
    [loadClasses, () => { if (classId !== null) loadSummary(selectedSequenceNo); }],
    [loadClasses, loadSummary, classId, selectedSequenceNo],
  );

  useEffect(() => {
    if (classId !== null) {
      loadSummary(selectedSequenceNo);
    }
  }, [classId, selectedSequenceNo, loadSummary]);

  const handleEditRow = (row) => {
    setEditingRow(row);
    setDisciplineFormData({
      warnings: row.manual?.warnings ?? 0,
      justifiedLateness: row.manual?.justifiedLateness ?? 0,
      unjustifiedLateness: row.manual?.unjustifiedLateness ?? 0,
      detention: 0,
      suspension: row.manual?.suspension ?? 0
    });
    setDisciplineDialogOpen(true);
  };

  const handleSaveRecord = async () => {
    if (!editingRow) return;
    try {
      setSaving(true);
      const body = {
        studentId: editingRow.studentId,
        sequenceNo: selectedSequenceNo,
        warnings: Number(disciplineFormData.warnings || 0),
        ...(!isPrimaryClass ? {
          justifiedLateness: Number(disciplineFormData.justifiedLateness || 0),
          unjustifiedLateness: Number(disciplineFormData.unjustifiedLateness || 0),
          detention: Number(disciplineFormData.detention || 0),
          suspension: Number(disciplineFormData.suspension || 0)
        } : {})
      };
      if (editingRow.manualRecordId) {
        await fetchJson(`/api/class-discipline/${editingRow.manualRecordId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      } else {
        await fetchJson('/api/class-discipline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }
      enqueueSnackbar(t('classDisciplinePage.snack.updated'), { variant: 'success' });
      setDisciplineDialogOpen(false);
      loadSummary(selectedSequenceNo);
    } catch (err) {
      console.error('Error saving discipline record:', err);
      enqueueSnackbar(t('classDisciplinePage.errors.saveFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const formatMinutes = (value) => minutesToHHMM(value || 0);
  const totalUnjustifiedAbsences = summaryRows.reduce((s, r) => s + (r.unjustifiedAbsenceMinutes || 0), 0);
  const totalLate = summaryRows.reduce((s, r) => s + ((r?.manual?.justifiedLateness != null || r?.manual?.unjustifiedLateness != null)
    ? ((r?.manual?.justifiedLateness || 0) + (r?.manual?.unjustifiedLateness || 0))
    : (r.lateMinutes || 0)), 0);
  const totalWarnings = summaryRows.reduce((s, r) => s + (r.manual?.warnings || 0), 0);
  const totalSuspensions = summaryRows.reduce((s, r) => s + (r.manual?.suspension || 0) + (r.manual?.exclusion ? 1 : 0), 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <GavelIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            {t('classDisciplinePage.title')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('classDisciplinePage.subtitle')}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary">{summaryRows.length}</Typography>
              <Typography variant="body2" color="textSecondary">{t('classDisciplinePage.summary.studentsInClass')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">{formatMinutes(totalUnjustifiedAbsences)}</Typography>
              <Typography variant="body2" color="textSecondary">{t('classDisciplinePage.summary.totalUnjustifiedAbsences')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">{formatMinutes(totalLate)}</Typography>
              <Typography variant="body2" color="textSecondary">{t('classDisciplinePage.summary.totalLate')}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error.main">{totalSuspensions}</Typography>
              <Typography variant="body2" color="textSecondary">{t('classDisciplinePage.summary.suspensionsExclusions')}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sequence Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="h6">{t('classDisciplinePage.sectionTitle')}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Sequence</InputLabel>
              <Select
                value={selectedSequenceNo}
                label="Sequence"
                onChange={(e) => setSelectedSequenceNo(e.target.value)}
              >
                {SEQ_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>Sequence {n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Discipline Summary Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>{t('classDisciplinePage.fields.student')}</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.unjustifiedAbsences')}</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.justifiedAbsences')}</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.lateness')}</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.warnings')}</TableCell>
                  {!isPrimaryClass && (
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.suspensions')}</TableCell>
                  )}
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.exclusion')}</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">{t('classDisciplinePage.fields.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                        No students found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  summaryRows.map((row, idx) => (
                    <TableRow key={row.studentId} hover sx={{ bgcolor: idx % 2 === 0 ? 'background.paper' : 'action.hover' }}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">{row.studentName}</Typography>
                            {row.studentNumber && (
                              <Typography variant="caption" color="textSecondary">{row.studentNumber}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color={row.unjustifiedAbsenceMinutes > 0 ? 'error.main' : 'text.primary'} fontWeight={row.unjustifiedAbsenceMinutes > 0 ? 700 : 400}>
                          {formatMinutes(row.unjustifiedAbsenceMinutes || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{formatMinutes(row.justifiedAbsenceMinutes || 0)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ color: ((row?.manual?.justifiedLateness || 0) + (row?.manual?.unjustifiedLateness || 0) + (row.lateMinutes || 0)) > 0 ? 'warning.main' : 'text.primary' }}>
                          {!isPrimaryClass && (
                            <Typography variant="caption" display="block">
                              {t('studentsDisciplinePage.table.justified')}: {formatMinutes(row?.manual?.justifiedLateness || 0)}
                            </Typography>
                          )}
                          <Typography variant="caption" display="block">
                            {t('studentsDisciplinePage.table.unjustified')}: {formatMinutes(
                              (row?.manual?.justifiedLateness != null || row?.manual?.unjustifiedLateness != null)
                                ? (row?.manual?.unjustifiedLateness || 0)
                                : (row?.lateMinutes || 0)
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {(row.manual?.warnings || 0) > 0 ? (
                          <Chip label={row.manual.warnings} color="warning" size="small" />
                        ) : (
                          <Typography variant="body2">0</Typography>
                        )}
                      </TableCell>
                      {!isPrimaryClass && (
                        <TableCell align="center">
                          {(row.manual?.suspension || 0) > 0 ? (
                            <Chip label={row.manual.suspension} color="error" size="small" />
                          ) : (
                            <Typography variant="body2">0</Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        {row.manual?.exclusion ? (
                          <Chip label="Yes" color="error" size="small" />
                        ) : (
                          <Chip label="No" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('classDisciplinePage.actions.editRecord')}>
                          <IconButton size="small" color="primary" disabled={pastYear} onClick={() => handleEditRow(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Discipline Dialog */}
      <Dialog open={disciplineDialogOpen} onClose={() => setDisciplineDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRow ? `Discipline — ${editingRow.studentName}` : t('classDisciplinePage.dialog.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t('classDisciplinePage.fields.warnings')}
                value={disciplineFormData.warnings}
                onChange={(e) => setDisciplineFormData({ ...disciplineFormData, warnings: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            {!isPrimaryClass && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('classDisciplinePage.fields.suspensions')}
                    value={disciplineFormData.suspension}
                    onChange={(e) => setDisciplineFormData({ ...disciplineFormData, suspension: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('classDisciplinePage.fields.justifiedLateness')}
                    value={disciplineFormData.justifiedLateness}
                    onChange={(e) => setDisciplineFormData({ ...disciplineFormData, justifiedLateness: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('classDisciplinePage.fields.unjustifiedLateness')}
                    value={disciplineFormData.unjustifiedLateness}
                    onChange={(e) => setDisciplineFormData({ ...disciplineFormData, unjustifiedLateness: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisciplineDialogOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveRecord}
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? t('common.saving') : t('classDisciplinePage.actions.updateRecord')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassDisciplinePage;
